export type StsTestKey = '5xSTS' | 'STS_30S' | 'STS_1MIN';
export type StsWorkflowStatus = 'completed' | 'awaiting_second_attempt';

export type StsBorgVitals = {
  borgDyspneaPre: number | null;
  borgLegPre: number | null;
  spo2Pre: number | null;
  hrPre: number | null;
  borgDyspneaPost: number | null;
  borgLegPost: number | null;
  spo2Post: number | null;
  hrPost: number | null;
};

import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export type FiveSTSResultPayload = {
  attempts: number[];
  best: number | null;
  chairHeightCm: number | null;
  usedArms: boolean;
  workflowStatus?: 'completed';
  finishedAt?: string;
  patientContext?: {
    birthDate?: string | null;
    ageYears?: number | null;
    sex?: string | null;
    weightKg?: number | null;
    heightCm?: number | null;
    identifier?: string | null;
  } | null;
  professionalContext?: {
    name?: string | null;
    registration?: string | null;
    role?: string | null;
  } | null;
};

export type StsRepetitionAttemptPayload = StsBorgVitals & {
  reps: number;
  chairHeightCm: number | null;
  finishedAt?: string;
};

export type StsRepetitionResultPayload = {
  attempts: number[];
  best: number | null;
  attempt1: StsRepetitionAttemptPayload | null;
  attempt2: StsRepetitionAttemptPayload | null;
  restIntervalSeconds: number | null;
  workflowStatus?: StsWorkflowStatus;
  finishedAt?: string;
  patientContext?: {
    birthDate?: string | null;
    ageYears?: number | null;
    sex?: string | null;
    weightKg?: number | null;
    heightCm?: number | null;
    identifier?: string | null;
  } | null;
  professionalContext?: {
    name?: string | null;
    registration?: string | null;
    role?: string | null;
  } | null;
};

export type AnyStsResultPayload = FiveSTSResultPayload | StsRepetitionResultPayload;

export type StsReferenceComparison = {
  source: 'Furlanetto2022';
  predictedValue: number;
  percentPredicted: number;
  normalLimitValue: number;
  normalLimitPercent: 128 | 78 | 77;
  normalLimitDirection: 'upper' | 'lower';
  withinNormalLimit: boolean;
  ageYears: number;
  bmiKgM2: number;
  heightCm: number;
  sexBinary: 0 | 1 | null;
  ageWithinReferenceRange: boolean;
  bmiWithinReferenceRange: boolean;
  equationLabel: string;
};

export type StsSummary = {
  testKey: StsTestKey;
  title: string;
  measurementLabel: string;
  measurementUnit: string;
  lowerIsBetter: boolean;
  best: number | null;
  validAttempts: number[];
  attemptCount: number;
  chairHeightCm: number | null;
  usedArms: boolean | null;
  restIntervalSeconds: number | null;
  workflowStatus: StsWorkflowStatus | 'completed';
  completed: boolean;
  severity: string;
  severityTone: string;
  scoreColor: string;
  interpretation: string;
  referenceComparison: StsReferenceComparison | null;
  attempt1?: StsRepetitionAttemptPayload | null;
  attempt2?: StsRepetitionAttemptPayload | null;
};

export const STS_REFERENCE_MIN_AGE_YEARS = 20;
export const STS_REFERENCE_MAX_AGE_YEARS = 80;
export const STS_REFERENCE_MIN_BMI_KG_M2 = 18;
export const STS_REFERENCE_MAX_BMI_KG_M2 = 40;
export const STS_REFERENCE_CHAIR_HEIGHT_CM = 46;

export const STS_BORG_VALUES: number[] = [0, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const STS_BORG_LABELS: Record<number, string> = {
  0: 'Nenhuma',
  0.5: 'Muito, muito leve',
  1: 'Muito leve',
  2: 'Leve',
  3: 'Moderada',
  4: 'Pouco intensa',
  5: 'Intensa',
  6: '',
  7: 'Muito intensa',
  8: '',
  9: 'Muito, muito intensa',
  10: 'Maxima',
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeAttemptSeries = (values: unknown, decimals = 0): number[] =>
  Array.isArray(values)
    ? values
        .map((value) => toNumber(value))
        .filter((value): value is number => value != null && value > 0)
        .map((value) => Number(value.toFixed(decimals)))
    : [];

const normalizeChairHeight = (value: unknown): number | null => {
  const parsed = toNumber(value);
  if (parsed == null || parsed <= 0) return null;
  return Math.round(parsed);
};

const normalizeRepetitionAttempt = (attempt: unknown): StsRepetitionAttemptPayload | null => {
  if (!attempt || typeof attempt !== 'object') return null;
  const source = attempt as Record<string, unknown>;
  const reps = toNumber(source.reps);

  if (reps == null || reps <= 0) return null;

  return {
    reps: Math.round(reps),
    chairHeightCm: normalizeChairHeight(source.chairHeightCm),
    borgDyspneaPre: toNumber(source.borgDyspneaPre),
    borgLegPre: toNumber(source.borgLegPre),
    spo2Pre: toNumber(source.spo2Pre),
    hrPre: toNumber(source.hrPre),
    borgDyspneaPost: toNumber(source.borgDyspneaPost),
    borgLegPost: toNumber(source.borgLegPost),
    spo2Post: toNumber(source.spo2Post),
    hrPost: toNumber(source.hrPost),
    finishedAt: typeof source.finishedAt === 'string' ? source.finishedAt : undefined,
  };
};

const inferWorkflowStatus = (testKey: StsTestKey, payload: StsRepetitionResultPayload): StsWorkflowStatus => {
  if (payload.workflowStatus === 'awaiting_second_attempt' || payload.workflowStatus === 'completed') {
    return payload.workflowStatus;
  }

  if (testKey === 'STS_30S' && payload.attempt2) return 'completed';
  if (testKey === 'STS_30S') return 'awaiting_second_attempt';
  if (payload.attempt2) return 'completed';
  return payload.attempt1 ? 'awaiting_second_attempt' : 'completed';
};

export const getStsTestLabel = (testKey: StsTestKey): string => {
  switch (testKey) {
    case '5xSTS':
      return 'Teste de Sentar e Levantar de 5 repetições';
    case 'STS_30S':
      return 'Teste de Sentar e Levantar de 30 segundos';
    case 'STS_1MIN':
      return 'Teste de Sentar e Levantar de 1 minuto';
    default:
      return testKey;
  }
};

export const formatBorgValue = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return String(value).replace('.', ',');
};

export const formatStsPrimaryValue = (testKey: StsTestKey, value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  if (testKey === '5xSTS') return `${value.toFixed(2)} segundos`;
  return formatStsRepetitionCount(value);
};

export const formatStsRepetitionCount = (value: number): string => {
  const repetitions = Math.round(value);
  return `${repetitions} ${repetitions === 1 ? 'repetição' : 'repetições'}`;
};

export const formatStsRestInterval = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0) return '—';
  if (value < 60) {
    const seconds = Math.round(value);
    return `${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`;
  }
  const minutes = Math.floor(value / 60);
  const seconds = Math.round(value % 60);
  const minutesLabel = `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  return seconds > 0
    ? `${minutesLabel} e ${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`
    : minutesLabel;
};

export const normalizeFiveSTSPayload = (payload: unknown): FiveSTSResultPayload | null => {
  if (!payload || typeof payload !== 'object') return null;
  const source = payload as Record<string, unknown>;
  const attempts = normalizeAttemptSeries(source.attempts, 2);
  const best = toNumber(source.best) ?? (attempts.length ? Math.min(...attempts) : null);

  return {
    attempts,
    best: best != null ? Number(best.toFixed(2)) : null,
    chairHeightCm: normalizeChairHeight(source.chairHeightCm),
    usedArms: Boolean(source.usedArms),
    workflowStatus: 'completed',
    finishedAt: typeof source.finishedAt === 'string' ? source.finishedAt : undefined,
    patientContext: source.patientContext && typeof source.patientContext === 'object' ? (source.patientContext as any) : undefined,
    professionalContext:
      source.professionalContext && typeof source.professionalContext === 'object' ? (source.professionalContext as any) : undefined,
  };
};

export const normalizeStsRepetitionPayload = (
  testKey: Extract<StsTestKey, 'STS_30S' | 'STS_1MIN'>,
  payload: unknown,
): StsRepetitionResultPayload | null => {
  if (!payload || typeof payload !== 'object') return null;
  const source = payload as Record<string, unknown>;
  const attempt1 = normalizeRepetitionAttempt(source.attempt1);
  const attempt2 = normalizeRepetitionAttempt(source.attempt2);
  const attempts = normalizeAttemptSeries(source.attempts).slice(0, 2);

  if (attempt1 && attempts[0] == null) attempts[0] = attempt1.reps;
  if (attempt2 && attempts[1] == null) attempts[1] = attempt2.reps;

  const validAttempts = attempts.filter((value) => typeof value === 'number' && value > 0);
  const best = toNumber(source.best) ?? (validAttempts.length ? Math.max(...validAttempts) : null);
  const normalized: StsRepetitionResultPayload = {
    attempts,
    best: best != null ? Math.round(best) : null,
    attempt1,
    attempt2,
    restIntervalSeconds: toNumber(source.restIntervalSeconds),
    finishedAt: typeof source.finishedAt === 'string' ? source.finishedAt : undefined,
    workflowStatus: 'completed',
    patientContext: source.patientContext && typeof source.patientContext === 'object' ? (source.patientContext as any) : undefined,
    professionalContext:
      source.professionalContext && typeof source.professionalContext === 'object' ? (source.professionalContext as any) : undefined,
  };

  normalized.workflowStatus = inferWorkflowStatus(testKey, normalized);
  return normalized;
};

export const normalizeStsPayload = (testKey: StsTestKey, payload: unknown): AnyStsResultPayload | null => {
  if (testKey === '5xSTS') return normalizeFiveSTSPayload(payload);
  return normalizeStsRepetitionPayload(testKey, payload);
};

const resolveStsSexBinary = (value: unknown): 0 | 1 | null => {
  const normalized = String(value ?? '').trim().toLocaleLowerCase('pt-BR');
  if (!normalized) return null;
  if (normalized === 'f' || normalized.startsWith('fem') || normalized.startsWith('mulher') || normalized.startsWith('woman')) {
    return 0;
  }
  if (normalized === 'm' || normalized.startsWith('masc') || normalized.startsWith('homem') || normalized.startsWith('man')) {
    return 1;
  }
  return null;
};

export const calculateStsReferenceComparison = (
  testKey: StsTestKey,
  observedValue: number | null | undefined,
  patientContext?: Record<string, unknown> | null,
): StsReferenceComparison | null => {
  if (!patientContext || typeof patientContext !== 'object') return null;

  const ageYears = toNumber(patientContext.ageYears ?? patientContext.age);
  const heightCm = toNumber(patientContext.heightCm ?? patientContext.height);
  const weightKg = toNumber(patientContext.weightKg ?? patientContext.weight);
  const explicitBmi = toNumber(patientContext.bmiKgM2 ?? patientContext.bmi);
  const bmiKgM2 =
    explicitBmi ??
    (weightKg != null && heightCm != null && heightCm > 0 ? weightKg / Math.pow(heightCm / 100, 2) : null);
  const sexBinary = resolveStsSexBinary(patientContext.sex);

  if (ageYears == null || heightCm == null || bmiKgM2 == null || ageYears < 0 || heightCm <= 0 || bmiKgM2 <= 0) {
    return null;
  }
  if (testKey === 'STS_1MIN' && sexBinary == null) return null;

  let predictedRaw: number;
  let normalLimitPercent: 128 | 78 | 77;
  let normalLimitDirection: 'upper' | 'lower';
  let equationLabel: string;

  if (testKey === '5xSTS') {
    predictedRaw = 2.711 + ageYears * 0.064 + bmiKgM2 * 0.132;
    normalLimitPercent = 128;
    normalLimitDirection = 'upper';
    equationLabel = '2,711 + (0,064 × idade) + (0,132 × IMC)';
  } else if (testKey === 'STS_30S') {
    predictedRaw = 27.633 - ageYears * 0.069 - bmiKgM2 * 0.283;
    normalLimitPercent = 78;
    normalLimitDirection = 'lower';
    equationLabel = '27,633 − (0,069 × idade) − (0,283 × IMC)';
  } else {
    predictedRaw =
      85.964 - ageYears * 0.186 - bmiKgM2 * 0.63 - heightCm * 0.175 + (sexBinary ?? 0) * 2.247;
    normalLimitPercent = 77;
    normalLimitDirection = 'lower';
    equationLabel =
      '85,964 − (0,186 × idade) − (0,630 × IMC) − (0,175 × altura em cm) + (2,247 × sexo)';
  }

  if (!Number.isFinite(predictedRaw) || predictedRaw <= 0) return null;

  const observed = toNumber(observedValue);
  const normalLimitRaw = predictedRaw * (normalLimitPercent / 100);
  const withinNormalLimit =
    observed != null &&
    (normalLimitDirection === 'upper' ? observed <= normalLimitRaw : observed >= normalLimitRaw);

  return {
    source: 'Furlanetto2022',
    predictedValue: Number(predictedRaw.toFixed(1)),
    percentPredicted: observed != null ? Number(((observed / predictedRaw) * 100).toFixed(0)) : 0,
    normalLimitValue: Number(normalLimitRaw.toFixed(1)),
    normalLimitPercent,
    normalLimitDirection,
    withinNormalLimit,
    ageYears: Number(ageYears.toFixed(0)),
    bmiKgM2: Number(bmiKgM2.toFixed(1)),
    heightCm: Number(heightCm.toFixed(1)),
    sexBinary,
    ageWithinReferenceRange:
      ageYears >= STS_REFERENCE_MIN_AGE_YEARS && ageYears <= STS_REFERENCE_MAX_AGE_YEARS,
    bmiWithinReferenceRange:
      bmiKgM2 >= STS_REFERENCE_MIN_BMI_KG_M2 && bmiKgM2 <= STS_REFERENCE_MAX_BMI_KG_M2,
    equationLabel,
  };
};

export const formatStsReferenceNumber = (value: number, decimals = 1): string =>
  Number(value.toFixed(decimals)).toString().replace('.', ',');

export const getStsReferenceValidationNote = (comparison: StsReferenceComparison): string => {
  const outside: string[] = [];
  if (!comparison.ageWithinReferenceRange) {
    outside.push(
      `idade de ${formatStsReferenceNumber(comparison.ageYears, 0)} anos fora da faixa de ${STS_REFERENCE_MIN_AGE_YEARS} a ${STS_REFERENCE_MAX_AGE_YEARS} anos`,
    );
  }
  if (!comparison.bmiWithinReferenceRange) {
    outside.push(
      `IMC de ${formatStsReferenceNumber(comparison.bmiKgM2)} kg/m² fora da faixa de ${STS_REFERENCE_MIN_BMI_KG_M2} a ${STS_REFERENCE_MAX_BMI_KG_M2} kg/m²`,
    );
  }
  return outside.length ? `Estimativa extrapolada: ${outside.join(' e ')}.` : '';
};

export const getStsReferenceUnit = (testKey: StsTestKey, value: number): string => {
  if (testKey === '5xSTS') return `${formatStsReferenceNumber(value)} segundos`;
  return `${formatStsReferenceNumber(value)} repetições`;
};

export const isStsWorkflowCompleted = (testKey: StsTestKey, payload: unknown): boolean => {
  const normalized = normalizeStsPayload(testKey, payload);
  if (!normalized) return false;

  if (testKey === '5xSTS') {
    return normalized.best != null && normalized.attempts.length > 0;
  }

  const repetitionPayload = normalized as StsRepetitionResultPayload;
  if (testKey === 'STS_30S') {
    return repetitionPayload.workflowStatus === 'completed' && Boolean(repetitionPayload.attempt2);
  }

  return repetitionPayload.workflowStatus === 'completed' && repetitionPayload.attempts.some((value) => value > 0);
};

const getFiveSTSSeverity = (best: number | null, referenceComparison?: StsReferenceComparison | null) => {
  if (best == null) return { label: 'Sem conclusao', tone: 'bg-slate-100 text-slate-700', color: '#475569' };
  if (referenceComparison) {
    return referenceComparison.withinNormalLimit
      ? { label: 'Dentro do limite de normalidade', tone: 'bg-emerald-100 text-emerald-700', color: '#10b981' }
      : { label: 'Acima do limite de normalidade', tone: 'bg-rose-100 text-rose-700', color: '#dc2626' };
  }
  if (best <= 12) return { label: 'Faixa esperada', tone: 'bg-emerald-100 text-emerald-700', color: '#10b981' };
  if (best <= 15) return { label: 'Acima do ponto de corte', tone: 'bg-amber-100 text-amber-700', color: '#f59e0b' };
  return { label: 'Risco funcional aumentado', tone: 'bg-rose-100 text-rose-700', color: '#dc2626' };
};

const getRepeatedStsSeverity = (
  attemptCount: number,
  workflowStatus: StsWorkflowStatus,
  completed: boolean,
  referenceComparison?: StsReferenceComparison | null,
) => {
  if (attemptCount === 0) return { label: 'Sem conclusao', tone: 'bg-slate-100 text-slate-700', color: '#475569' };
  if (!completed || workflowStatus === 'awaiting_second_attempt') {
    return { label: '1a tentativa salva', tone: 'bg-amber-100 text-amber-700', color: '#f59e0b' };
  }
  if (referenceComparison) {
    return referenceComparison.withinNormalLimit
      ? { label: 'Dentro do limite de normalidade', tone: 'bg-emerald-100 text-emerald-700', color: '#10b981' }
      : { label: 'Abaixo do limite de normalidade', tone: 'bg-rose-100 text-rose-700', color: '#dc2626' };
  }
  if (attemptCount >= 2) {
    return { label: '2 tentativas registradas', tone: 'bg-emerald-100 text-emerald-700', color: '#10b981' };
  }
  return { label: '1 tentativa registrada', tone: 'bg-cyan-100 text-cyan-700', color: '#0891b2' };
};

export const summarizeStsResult = (testKey: StsTestKey, payload: unknown): StsSummary => {
  if (testKey === '5xSTS') {
    const normalized = normalizeFiveSTSPayload(payload);
    const referenceComparison = calculateStsReferenceComparison(
      testKey,
      normalized?.best,
      normalized?.patientContext as Record<string, unknown> | null | undefined,
    );
    const severity = getFiveSTSSeverity(normalized?.best ?? null, referenceComparison);
    const validationNote = referenceComparison ? getStsReferenceValidationNote(referenceComparison) : '';
    const automaticInterpretation =
      normalized?.best != null
        ? referenceComparison
          ? `Melhor tempo de ${normalized.best.toFixed(2)} segundos. Previsto: ${getStsReferenceUnit(testKey, referenceComparison.predictedValue)}; limite superior de normalidade: ${getStsReferenceUnit(testKey, referenceComparison.normalLimitValue)}. O tempo corresponde a ${formatStsReferenceNumber(referenceComparison.percentPredicted, 0)}% do previsto.${validationNote ? ` ${validationNote}` : ''}`
          : `Melhor tempo registrado de ${normalized.best.toFixed(2)} segundos. Tempos mais altos sugerem pior desempenho funcional em membros inferiores e maior risco de quedas.`
        : 'Sem tentativas válidas suficientes para interpretar o 5xSTS.';
    return {
      testKey,
      title: getStsTestLabel(testKey),
      measurementLabel: 'Tempo principal',
      measurementUnit: 'segundos',
      lowerIsBetter: true,
      best: normalized?.best ?? null,
      validAttempts: normalized?.attempts ?? [],
      attemptCount: normalized?.attempts.length ?? 0,
      chairHeightCm: normalized?.chairHeightCm ?? null,
      usedArms: normalized?.usedArms ?? null,
      restIntervalSeconds: 60,
      workflowStatus: 'completed',
      completed: normalized?.best != null,
      severity: severity.label,
      severityTone: severity.tone,
      scoreColor: severity.color,
      interpretation: resolveAssessmentInterpretationText(normalized, automaticInterpretation),
      referenceComparison,
    };
  }

  const normalized = normalizeStsRepetitionPayload(testKey, payload);
  const workflowStatus = normalized?.workflowStatus ?? 'completed';
  const completed = isStsWorkflowCompleted(testKey, normalized);
  const referenceComparison = calculateStsReferenceComparison(
    testKey,
    normalized?.best,
    normalized?.patientContext as Record<string, unknown> | null | undefined,
  );
  const severity = getRepeatedStsSeverity(
    normalized?.attempts.filter((value) => value > 0).length ?? 0,
    workflowStatus,
    completed,
    referenceComparison,
  );
  const validationNote = referenceComparison ? getStsReferenceValidationNote(referenceComparison) : '';
  const automaticInterpretation =
    testKey === 'STS_30S'
      ? normalized?.best != null
        ? referenceComparison
          ? `Melhor desempenho de ${formatStsRepetitionCount(normalized.best)}. Previsto: ${getStsReferenceUnit(testKey, referenceComparison.predictedValue)}; limite inferior de normalidade: ${getStsReferenceUnit(testKey, referenceComparison.normalLimitValue)}. O resultado corresponde a ${formatStsReferenceNumber(referenceComparison.percentPredicted, 0)}% do previsto.${validationNote ? ` ${validationNote}` : ''}`
          : `Melhor desempenho de ${formatStsRepetitionCount(normalized.best)} no teste de 30 segundos. Valores mais altos indicam melhor resistência funcional de membros inferiores.`
        : 'Sem tentativas válidas suficientes para interpretar o STS 30 s.'
      : normalized?.best != null
        ? referenceComparison
          ? `Melhor desempenho de ${formatStsRepetitionCount(normalized.best)}. Previsto: ${getStsReferenceUnit(testKey, referenceComparison.predictedValue)}; limite inferior de normalidade: ${getStsReferenceUnit(testKey, referenceComparison.normalLimitValue)}. O resultado corresponde a ${formatStsReferenceNumber(referenceComparison.percentPredicted, 0)}% do previsto.${validationNote ? ` ${validationNote}` : ''}`
          : `Melhor desempenho de ${formatStsRepetitionCount(normalized.best)} no teste de 1 minuto. O teste descreve resistência funcional e tolerância ao esforço em tarefas repetitivas.`
        : 'Sem tentativas válidas suficientes para interpretar o STS 1 min.';

  return {
    testKey,
    title: getStsTestLabel(testKey),
    measurementLabel: 'Melhor marca',
    measurementUnit: 'repetições',
    lowerIsBetter: false,
    best: normalized?.best ?? null,
    validAttempts: normalized?.attempts.filter((value) => value > 0) ?? [],
    attemptCount: normalized?.attempts.filter((value) => value > 0).length ?? 0,
    chairHeightCm: normalized?.attempt1?.chairHeightCm ?? normalized?.attempt2?.chairHeightCm ?? null,
    usedArms: null,
    restIntervalSeconds: normalized?.restIntervalSeconds ?? null,
    workflowStatus,
    completed,
    severity: severity.label,
    severityTone: severity.tone,
    scoreColor: severity.color,
    interpretation: resolveAssessmentInterpretationText(normalized, automaticInterpretation),
    referenceComparison,
    attempt1: normalized?.attempt1 ?? null,
    attempt2: normalized?.attempt2 ?? null,
  };
};

export const getSTSSeverityLabel = (testKey: StsTestKey, payload: unknown): string =>
  summarizeStsResult(testKey, payload).severity;

export const getSTSSeverityTone = (testKey: StsTestKey, payload: unknown): string =>
  summarizeStsResult(testKey, payload).severityTone;

export const getSTSInterpretationText = (testKey: StsTestKey, payload: unknown): string =>
  summarizeStsResult(testKey, payload).interpretation;
